'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/hooks/use-session';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

/** Pages where the terms dialog should not overlay content (so users can read them). */
const TERMS_READABLE_PAGES = ['/agb', '/privacy-policy'];

export function TermsAcceptanceDialog() {
  const { t } = useTranslation();
  const { data: session, status } = useSession();
  const queryClient = useQueryClient();
  const pathname = usePathname();

  const [termsChecked, setTermsChecked] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const isAuthenticated = status === 'authenticated';
  const isTermsPage = TERMS_READABLE_PAGES.some(
    (p) => pathname === p || pathname?.endsWith(p),
  );
  const needsTerms = isAuthenticated && !session?.user?.termsAcceptedAt && !isTermsPage;

  async function handleAccept() {
    if (!termsChecked) return;
    setSubmitting(true);
    setSubmitError(false);

    try {
      const res = await fetch('/api/auth/accept-terms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ analyticsConsent: analyticsChecked }),
      });

      if (res.ok) {
        // Invalidate session query so useSession picks up the new values
        await queryClient.invalidateQueries({ queryKey: ['session-me'] });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (!needsTerms) return null;

  const tp = (key: string) => t(`terms.acceptance.${key}`);

  return (
    <Dialog open modal>
      <DialogContent
        className="sm:max-w-[500px] [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-nosnippet
      >
        <DialogHeader className="pb-1">
          <DialogTitle className="text-xl">{tp('title')}</DialogTitle>
          <DialogDescription className="mt-1">
            {tp('subtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Checkbox 1: AGB / Terms */}
          <div className="flex items-center gap-3">
            <Checkbox
              id="terms-accept"
              checked={termsChecked}
              onCheckedChange={(v) => setTermsChecked(v === true)}
              className="shrink-0 border-foreground/40"
            />
            <Label
              htmlFor="terms-accept"
              className="text-xs leading-relaxed cursor-pointer"
            >
              {tp('termsLabel')}{' '}
              <a
                href="/agb"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                {tp('termsLink')}
              </a>
              {' *'}
            </Label>
          </div>

          {/* Checkbox 2: Analytics consent */}
          <div className="flex items-center gap-3">
            <Checkbox
              id="analytics-accept"
              checked={analyticsChecked}
              onCheckedChange={(v) => setAnalyticsChecked(v === true)}
              className="shrink-0 border-foreground/40"
            />
            <Label
              htmlFor="analytics-accept"
              className="text-xs leading-relaxed cursor-pointer"
            >
              {tp('analyticsLabel')}
            </Label>
          </div>

          {/* Privacy policy acknowledgment (no checkbox) */}
          <p className="text-xs text-muted-foreground">
            {tp('privacyNotice')}{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {tp('privacyLink')}
            </a>
            .
          </p>
        </div>

        {submitError && <p role="alert" className="text-sm text-destructive">{tp('saveFailed')}</p>}
        <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
          <Button variant="outline" asChild>
            <a href="/auth/logout">{tp('logout')}</a>
          </Button>
          <Button
            onClick={handleAccept}
            disabled={!termsChecked || submitting}
          >
            {submitting ? tp('submitting') : tp('submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
